import {
  Navbar,
  NavbarGroup,
  Alignment,
  NavbarHeading,
  Button,
  Classes,
  Menu,
  PopoverInteractionKind,
} from "@blueprintjs/core";
import { Link } from "react-router-dom";
import { useLocation, Location } from "react-router";
import { constructPath, RouteProps } from "routes";
import { RouteType } from "routes/types";
import { Node } from "utils/tree";
import { Popover2 } from "@blueprintjs/popover2";
import { ConditionalWrapper } from "utils/utils";
import React, { useState } from "react";
import { concat } from "lodash";

export interface NavProps extends RouteProps {
  label?: string;
  showChildren?: boolean;
  isMobile?: boolean;
  onClose?: () => void;
}

const hasChildren = (node: Node<RouteType>): boolean => !node.isLeaf();

const findNavRoute = (node: Node<RouteType> | undefined): Node<RouteType> | undefined => {
  while (node && node?.data?.parentId !== 0) {
    node = node?.parent;
  }
  return node;
};

export const isActive = (location: Location, node: Node<RouteType>): boolean =>
  concat([constructPath(node)], ...node.children.map((c) => constructPath(c))).includes(location.pathname);

function Navigation(props: NavProps) {
  const { node, label, showChildren, isMobile, onClose } = props;
  const location = useLocation();
  const parent = findNavRoute(node);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const toggleMenu = (menuName: string | undefined) => {
    if (!menuName) return;
    setExpandedMenus(prev => 
      prev.includes(menuName) 
        ? prev.filter(name => name !== menuName)
        : [...prev, menuName]
    );
  };

  const renderLink = (child: Node<RouteType>, primary: boolean) => {
    const { data } = child;
    if (data) {
      const name = String(data.name);
      return (
        <Link key={name} to={constructPath(child)} onClick={isMobile ? onClose : undefined}>
          <Button
            key={name}
            minimal
            intent={primary ? "primary" : "none"}
            text={data.label}
            active={isActive(location, child)}
          />
        </Link>
      );
    }
  };

  const renderMobileMenu = (children: Array<Node<RouteType>>) => {
    return children
      .filter((c) => !c.data?.hidden)
      .map((child) => {
        const name = child.data?.name ? String(child.data.name) : '';
        return (
          <div key={name} className="mobile-menu-item">
            {renderLink(child, false)}
          </div>
        );
      });
  };

  const renderDesktopMenu = (children: Array<Node<RouteType>>) => {
    const primary: boolean = false;
    return (
      <React.Fragment>
        {children
          .filter((c) => !c.data?.hidden)
          .map((child) => {
            const name = child.data?.name ? String(child.data.name) : '';
            return (
              <ConditionalWrapper
                key={name}
                condition={Boolean(child.data)}
                wrapper={(c) => (
                  <Menu key={name} className={`${Classes.POPOVER_DISMISS} subNav`}>
                    {c}
                  </Menu>
                )}
              >
                {renderLink(child, primary)}
              </ConditionalWrapper>
            );
          })}
      </React.Fragment>
    );
  };

  const renderMobileLinks = (child: Node<RouteType>) => {
    const { data, children } = child;
    const name = data?.name ? String(data.name) : '';
    const isExpanded = expandedMenus.includes(name);

    return (
      <div key={name} className="mobile-menu-group">
        <Button
          minimal
          intent="primary"
          text={data?.label}
          active={isActive(location, child)}
          rightIcon={isExpanded ? "caret-up" : "caret-down"}
          onClick={() => toggleMenu(name)}
        />
        {isExpanded && (
          <div className="mobile-submenu">
            {renderMobileMenu(children)}
          </div>
        )}
      </div>
    );
  };

  const renderDesktopLinks = (child: Node<RouteType>) => {
    const { data, children } = child;
    const name = data?.name ? String(data.name) : '';
    return (
      <Popover2
        key={name}
        content={renderDesktopMenu(children)}
        placement={"bottom-start"}
        minimal
        interactionKind={PopoverInteractionKind.CLICK}
      >
        <Button
          key={name}
          minimal
          intent="primary"
          text={data?.label}
          active={isActive(location, child)}
          rightIcon="caret-down"
        />
      </Popover2>
    );
  };

  const primary: boolean = true;

  if (isMobile) {
    return (
      <div className="navigation">
        <div className="mobile-nav-content">
          {parent &&
            parent.children
              .filter((c) => c.data && !c.data.hidden)
              .map((child) => 
                showChildren && hasChildren(child) 
                  ? renderMobileLinks(child)
                  : renderLink(child, primary)
              )}
        </div>
      </div>
    );
  }

  return (
    <Navbar className="navigation">
      <NavbarGroup align={Alignment.RIGHT}>
        {label && <NavbarHeading>{label}</NavbarHeading>}
        {parent &&
          parent.children
            .filter((c) => c.data && !c.data.hidden)
            .map((child) => 
              showChildren && hasChildren(child) 
                ? renderDesktopLinks(child)
                : renderLink(child, primary)
            )}
      </NavbarGroup>
    </Navbar>
  );
}

export default Navigation;
